import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clienteService, prestamoService } from '../../services/api';
import api from '../../services/api';
import '../../styles/Clientes.css';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

// Función para formatear números con comas (15000 -> 15,000)
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  const number = value.toString().replace(/,/g, '');
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Función para quitar comas (15,000 -> 15000)
const removeCommas = (value) => {
  return value.toString().replace(/,/g, '');
};

const NuevoPrestamo = () => {
  usePermisosGuard('prestamos', 'crear');
  const navigate = useNavigate();
  const { codigo } = useParams();
  const getRoute = (path) => codigo ? `/${codigo}${path}` : path;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clientes, setClientes] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [rutas, setRutas] = useState([]);

  const [formData, setFormData] = useState({
    cliente_id: '',
    ruta_id: '',
    modalidad: 'Mensual',
    tipo: 'capital_final',
    monto_prestado: '',
    tasa: '',
    cuotas: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_efectividad: new Date().toISOString().split('T')[0],
    gastos_cierre: '0',
    tiene_mora: false,
    dias_libres: [], // Array para almacenar los días libres seleccionados
    cuota_manual: '' // Para Tipo San: cuota ingresada manualmente
  });

  const [calculado, setCalculado] = useState({
    capital: 0,
    interes: 0,
    total: 0,
    cuota: 0,
    capital_entregar: 0
  });

  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [tablaAmortizacion, setTablaAmortizacion] = useState([]);

  const tiposPrestamo = [
    { value: 'capital_final', label: 'Capital Final' },
    { value: 'capital_final_fijo', label: 'Capital Final Fijo' },
    { value: 'cuota_fija', label: 'Cuota Fija' },
    { value: 'amortizado_frances', label: 'Amortizado Frances' },
    { value: 'amortizado', label: 'Amortizado' },
    { value: 'tipo_san', label: 'Tipo San' }
  ];

  const modalidades = [
    { value: 'Diario', label: 'Diario' },
    { value: 'Semanal', label: 'Semanal' },
    { value: 'Quincenal', label: 'Quincenal' },
    { value: 'Mensual', label: 'Mensual' }
  ];

  // Función para obtener los tipos de préstamo disponibles según la modalidad
  const getTiposPrestamoPorModalidad = () => {
    if (formData.modalidad === 'Diario') {
      // Para modalidad Diario, solo mostrar Amortizado y Tipo San
      return tiposPrestamo.filter(tipo =>
        tipo.value === 'amortizado' || tipo.value === 'tipo_san'
      );
    }
    // Para otras modalidades, mostrar todos los tipos
    return tiposPrestamo;
  };

  // Función para determinar qué campos mostrar según el tipo de préstamo
  const shouldShowField = (fieldName) => {
    switch (fieldName) {
      case 'monto_cuota':
      case 'interes_total':
      case 'monto_total':
        return formData.tipo !== 'capital_final' && formData.tipo !== 'capital_final_fijo';

      case 'tasa':
        // Tipo San NO requiere tasa (sin interés)
        return formData.tipo === 'amortizado' ||
               formData.tipo === 'amortizado_frances' ||
               formData.tipo === 'interes_sobre_saldo' ||
               formData.tipo === 'capital_final' ||
               formData.tipo === 'capital_final_fijo' ||
               formData.tipo === 'cuota_fija';

      case 'interes':
        // Tipo San SÍ muestra interés (pero con valor 0, solo lectura)
        return formData.tipo === 'amortizado' ||
               formData.tipo === 'amortizado_frances' ||
               formData.tipo === 'capital_final' ||
               formData.tipo === 'capital_final_fijo' ||
               formData.tipo === 'cuota_fija' ||
               formData.tipo === 'tipo_san';

      case 'total':
        // Tipo San SÍ muestra total (igual al capital, solo lectura)
        return formData.tipo === 'amortizado' ||
               formData.tipo === 'amortizado_frances' ||
               formData.tipo === 'cuota_fija' ||
               formData.tipo === 'capital_final' ||
               formData.tipo === 'capital_final_fijo' ||
               formData.tipo === 'tipo_san';

      case 'cuotas':
        // Las cuotas NO se muestran para préstamos de capital final
        return formData.tipo !== 'capital_final' && formData.tipo !== 'capital_final_fijo';

      default:
        return true;
    }
  };

  useEffect(() => {
    loadClientes();
    loadRutas();

    // Calcular fecha de primer pago inicial basada en modalidad
    const hoy = new Date();
    let diasASumar = 0;

    switch(formData.modalidad) {
      case 'Diario':
        diasASumar = 1;  // Hoy + 1 día
        break;
      case 'Semanal':
        diasASumar = 7;
        break;
      case 'Quincenal':
        diasASumar = 15;
        break;
      case 'Mensual':
        diasASumar = 30;
        break;
      default:
        diasASumar = 0;
    }

    const fechaDesembolso = new Date(hoy);  // Fecha de desembolso = hoy
    const primerPago = new Date(hoy);
    primerPago.setDate(primerPago.getDate() + diasASumar);  // Primer pago = hoy + días según modalidad

    setFormData(prev => ({
      ...prev,
      fecha_inicio: fechaDesembolso.toISOString().split('T')[0],      // Fecha de desembolso
      fecha_efectividad: primerPago.toISOString().split('T')[0]        // Fecha del primer pago
    }));

    // Cerrar dropdown al hacer clic fuera
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clienteService.getClientes({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const loadRutas = async () => {
    try {
      const response = await api.get('/rutas');
      if (response.data.success) {
        setRutas(response.data.rutas || []);
        console.log('✅ Rutas cargadas:', response.data.rutas.length);
      }
    } catch (err) {
      console.error('❌ Error al cargar rutas:', err);
      // Si falla, usar lista vacía
      setRutas([]);
    }
  };

  // Filtrar clientes según búsqueda
  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10); // Mostrar solo los primeros 10 resultados

  const seleccionarCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setFormData({ ...formData, cliente_id: cliente.id });
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
  };

  const limpiarCliente = () => {
    setClienteSeleccionado(null);
    setFormData({ ...formData, cliente_id: '' });
    setBusquedaCliente('');
  };

  // Función para manejar la selección de días libres (máximo 2)
  const handleDiaLibreToggle = (dia) => {
    setFormData(prev => {
      const diasActuales = prev.dias_libres || [];
      const index = diasActuales.indexOf(dia);

      if (index > -1) {
        // Si ya está seleccionado, quitarlo
        return {
          ...prev,
          dias_libres: diasActuales.filter(d => d !== dia)
        };
      } else {
        // Si no está seleccionado, agregarlo solo si no se han seleccionado 2
        if (diasActuales.length < 2) {
          return {
            ...prev,
            dias_libres: [...diasActuales, dia]
          };
        }
        return prev; // No agregar más de 2
      }
    });
  };

  // Función para manejar el cambio de cuota manual (solo para Tipo San)
  // Permite calcular si una cuota es rentable (interés positivo = ganancia)
  const handleCuotaManualChange = (e) => {
    const cuota = parseFloat(e.target.value) || 0;
    const cuotas = parseInt(formData.cuotas) || 1;
    const capital = parseFloat(formData.monto_prestado) || 0;
    const gastos = parseFloat(formData.gastos_cierre) || 0;

    // Actualizar el valor de cuota_manual
    setFormData(prev => ({
      ...prev,
      cuota_manual: e.target.value
    }));

    if (cuota > 0 && cuotas > 0 && capital > 0) {
      // Total que se cobrará
      const total = cuota * cuotas;
      // Interés = Total - Capital (puede ser negativo si no es rentable)
      const interes = Math.round((total - capital) * 100) / 100;
      const capitalEntregar = capital - gastos;

      setCalculado({
        capital: capital,
        interes: interes,
        total: total,
        cuota: cuota,
        capital_entregar: capitalEntregar
      });
    }
  };

  const calcularPrestamo = useCallback(async () => {
    const monto = parseFloat(formData.monto_prestado) || 0;
    const tasa = parseFloat(formData.tasa) || 0;
    const cuotas = parseInt(formData.cuotas) || 1; // Valor por defecto 1 si no hay cuotas
    const gastos = parseFloat(formData.gastos_cierre) || 0;

    // Para Capital Final y Capital Final Fijo, calcular directamente sin llamar al backend
    if ((formData.tipo === 'capital_final' || formData.tipo === 'capital_final_fijo') && monto > 0 && tasa > 0) {
      // Para Capital Final Fijo, el interés se divide entre 2 (dos pagos quincenales)
      let interes = (monto * tasa) / 100;
      if (formData.tipo === 'capital_final_fijo') {
        interes = Math.round((interes / 2) * 100) / 100;
      }
      const total = monto + interes;
      const cuota = cuotas > 0 ? Math.round((total / cuotas) * 100) / 100 : 0;
      const capitalEntregar = monto - gastos;

      setCalculado({
        capital: monto,
        interes: interes,
        total: total,
        cuota: cuota,
        capital_entregar: capitalEntregar
      });
      return;
    }

    // Para Cuota Fija: Total = Capital + Interés + Gastos de Cierre
    if (formData.tipo === 'cuota_fija' && monto > 0 && tasa > 0 && cuotas > 0) {
      const interes = Math.round((monto * tasa / 100) * 100) / 100;
      const total = monto + interes + gastos;
      const cuota = Math.round((total / cuotas) * 100) / 100;
      const capitalEntregar = monto; // Cliente recibe monto completo

      setCalculado({
        capital: monto,
        interes: interes,
        total: total,
        cuota: cuota,
        capital_entregar: capitalEntregar
      });
      return;
    }

    // Para Tipo San: Interés = Capital / 3 (cuando se calcula desde Capital)
    // Si hay cuota_manual, permite ver si es rentable (interés puede ser negativo)
    if (formData.tipo === 'tipo_san' && cuotas > 0) {
      // Si hay cuota manual, calcular desde la cuota (ya calculado en handleCuotaManualChange)
      if (formData.cuota_manual && parseFloat(formData.cuota_manual) > 0 && monto > 0) {
        const cuota = parseFloat(formData.cuota_manual);
        const total = cuota * cuotas;
        const interes = Math.round((total - monto) * 100) / 100;
        const capitalEntregar = monto - gastos;

        setCalculado({
          capital: monto,
          interes: interes,
          total: total,
          cuota: cuota,
          capital_entregar: capitalEntregar
        });
      } else if (monto > 0) {
        // Si no hay cuota manual, calcular desde el capital con fórmula estándar
        const interes = Math.round((monto / 3) * 100) / 100;
        const total = monto + interes;
        const cuota = Math.round((total / cuotas) * 100) / 100;
        const capitalEntregar = monto - gastos;

        setCalculado({
          capital: monto,
          interes: interes,
          total: total,
          cuota: cuota,
          capital_entregar: capitalEntregar
        });
      }
      return;
    }

    // Para Amortizado y Amortizado Francés con suficientes datos, calcular directamente
    if ((formData.tipo === 'amortizado' || formData.tipo === 'amortizado_frances') && monto > 0 && tasa > 0 && cuotas > 0) {
      let interes = 0;
      if (formData.tipo === 'amortizado') {
        // Para préstamos DIARIOS: Interés = (Capital × Tasa) / 294
        // Para otros: Interés = (Capital × Tasa / 100) × Cuotas / 4
        if (formData.modalidad === 'Diario') {
          interes = Math.round(((monto * tasa) / 294) * 100) / 100;
        } else {
          interes = Math.round(((monto * (tasa / 100) * cuotas) / 4) * 100) / 100;
        }
      } else if (formData.tipo === 'amortizado_frances') {
        // Amortizado Francés: Interés = (Capital × Tasa / 100) × Cuotas / 3.388
        interes = Math.round(((monto * (tasa / 100) * cuotas) / 3.388) * 100) / 100;
      }
      const total = monto + interes;
      const cuota = Math.round((total / cuotas) * 100) / 100;
      const capitalEntregar = monto - gastos;

      setCalculado({
        capital: monto,
        interes: interes,
        total: total,
        cuota: cuota,
        capital_entregar: capitalEntregar
      });
      return;
    }

    // Para otros tipos, usar el cálculo del backend
    try {
      const response = await prestamoService.calcularPrestamo({
        monto: monto,
        tasa: tasa,
        cuotas: cuotas,
        tipo: formData.tipo,
        gastos_cierre: gastos
      });

      setCalculado({
        capital: response.data.capital,
        interes: response.data.interes,
        total: response.data.total,
        cuota: response.data.cuota_semanal,
        capital_entregar: response.data.capital_entregar
      });
    } catch (err) {
      console.error('Error al calcular préstamo:', err);
    }
  }, [formData.monto_prestado, formData.tasa, formData.cuotas, formData.tipo, formData.gastos_cierre]);

  useEffect(() => {
    if (formData.monto_prestado) {
      // Para Capital Final y Capital Final Fijo, calcular inmediatamente si tiene capital y tasa
      if ((formData.tipo === 'capital_final' || formData.tipo === 'capital_final_fijo') && formData.tasa !== '') {
        calcularPrestamo();
      }
      // Para Cuota Fija, calcular si tiene capital, tasa y cuotas
      else if (formData.tipo === 'cuota_fija' && formData.tasa !== '' && formData.cuotas) {
        calcularPrestamo();
      }
      // Para otros tipos, solo calcular si tiene los datos mínimos necesarios incluyendo cuotas
      else if (formData.cuotas && (formData.tipo === 'tipo_san' || formData.tasa !== '')) {
        calcularPrestamo();
      }
    }
  }, [formData.monto_prestado, formData.tasa, formData.cuotas, formData.tipo, formData.gastos_cierre, calcularPrestamo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // Si cambia la modalidad, calcular automáticamente el primer pago
    if (name === 'modalidad') {
      const hoy = new Date();
      let diasASumar = 0;

      switch(value) {
        case 'Diario':
          diasASumar = 1;  // Hoy + 1 día
          break;
        case 'Semanal':
          diasASumar = 7;
          break;
        case 'Quincenal':
          diasASumar = 15;
          break;
        case 'Mensual':
          diasASumar = 30;
          break;
        default:
          diasASumar = 0;
      }

      const primerPago = new Date(hoy);
      primerPago.setDate(primerPago.getDate() + diasASumar);

      // Calcular fecha de desembolso = hoy (fecha actual)
      const fechaDesembolso = new Date(hoy);

      // Si la modalidad es Diario y el tipo actual no es válido, resetear tipo
      let nuevoTipo = formData.tipo;
      if (value === 'Diario') {
        if (nuevoTipo !== 'amortizado' && nuevoTipo !== 'tipo_san') {
          nuevoTipo = 'amortizado'; // Valor por defecto para Diario
        }
      }

      setFormData(prev => ({
        ...prev,
        [name]: newValue,
        tipo: nuevoTipo,
        fecha_inicio: fechaDesembolso.toISOString().split('T')[0],  // Fecha de desembolso
        fecha_efectividad: primerPago.toISOString().split('T')[0]    // Fecha del primer pago
      }));
      return;
    }

    // Si cambia la fecha_inicio (desembolso), calcular fecha_efectividad (primer pago) automáticamente
    if (name === 'fecha_inicio' && value) {
      const fechaDesembolso = new Date(value);
      const primerPago = new Date(fechaDesembolso);

      // Calcular primer pago según modalidad
      switch(formData.modalidad) {
        case 'Diario':
          primerPago.setDate(primerPago.getDate() + 1);  // Siguiente día
          break;
        case 'Semanal':
          primerPago.setDate(primerPago.getDate() + 7);
          break;
        case 'Quincenal':
          primerPago.setDate(primerPago.getDate() + 15);
          break;
        case 'Mensual':
          primerPago.setDate(primerPago.getDate() + 30);
          break;
        default:
          primerPago.setDate(primerPago.getDate() + 1);
      }

      // Para Capital Final Fijo: primer pago = desembolso + 15 días
      if (formData.tipo === 'capital_final_fijo') {
        primerPago.setDate(fechaDesembolso.getDate() + 15);
      }

      setFormData(prev => ({
        ...prev,
        [name]: newValue,
        fecha_efectividad: primerPago.toISOString().split('T')[0]  // Fecha del primer pago
      }));
      return;
    }

    // Si cambia el tipo de préstamo, limpiar cuota_manual
    if (name === 'tipo') {
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
        cuota_manual: '' // Limpiar cuota manual al cambiar tipo
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Función para generar la tabla de amortización
  const generarTablaAmortizacion = () => {
    if (!formData.monto_prestado || !calculado.cuota || !formData.cuotas) {
      setError('❌ Complete todos los campos antes de generar la tabla');
      return;
    }

    const tabla = [];
    const cuota = parseFloat(calculado.cuota);
    const capitalTotal = parseFloat(calculado.capital);
    const interesTotal = parseFloat(calculado.interes);
    const totalPrestamo = parseFloat(calculado.total);
    const cuotas = parseInt(formData.cuotas);

    // Calcular capital e interés por cuota (distribución proporcional)
    const capitalPorCuota = capitalTotal / cuotas;
    const interesPorCuota = interesTotal / cuotas;

    let saldoPendiente = totalPrestamo;

    // Generar fechas según modalidad - usar fecha_efectividad (primer pago)
    const fechaPrimerPago = new Date(formData.fecha_efectividad);
    const diasLibres = formData.dias_libres || [];

    for (let i = 1; i <= cuotas; i++) {
      let fechaPago = new Date(fechaPrimerPago);

      // Calcular fecha según modalidad
      switch(formData.modalidad) {
        case 'Diario':
          fechaPago.setDate(fechaPrimerPago.getDate() + (i - 1));
          // Saltar días libres
          while (diasLibres.includes(fechaPago.getDay())) {
            fechaPago.setDate(fechaPago.getDate() + 1);
          }
          break;
        case 'Semanal':
          fechaPago.setDate(fechaPrimerPago.getDate() + ((i - 1) * 7));
          break;
        case 'Quincenal':
          fechaPago.setDate(fechaPrimerPago.getDate() + ((i - 1) * 15));
          break;
        case 'Mensual':
          fechaPago.setMonth(fechaPrimerPago.getMonth() + (i - 1));
          break;
        default:
          fechaPago.setDate(fechaPrimerPago.getDate() + (i - 1));
      }

      const nombresDias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const diaSemana = nombresDias[fechaPago.getDay()];

      // Restar la cuota del saldo ANTES de guardar
      saldoPendiente -= cuota;

      tabla.push({
        pago: i,
        cuota: cuota.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        capital: capitalPorCuota.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        interes: interesPorCuota.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        saldo: Math.max(0, saldoPendiente).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        fecha: fechaPago.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        diaSemana: diaSemana
      });
    }

    setTablaAmortizacion(tabla);
    setMostrarTabla(true);
  };

  // Función para ocultar la tabla
  const ocultarTabla = () => {
    setMostrarTabla(false);
  };

  // Función para descargar PDF con jsPDF
  const descargarPDF = () => {
    if (tablaAmortizacion.length === 0) {
      setError('❌ Primero debe generar la tabla de amortización');
      return;
    }

    const doc = new jsPDF();

    // Configurar fuente
    doc.setFontSize(10);

    // Encabezado izquierdo
    doc.text(`Cliente: ${clienteSeleccionado?.nombres || ''} ${clienteSeleccionado?.apellidos || ''}`, 14, 15);
    doc.text(`Modalidad: ${formData.modalidad}`, 14, 20);
    doc.text(`Tipo: ${tiposPrestamo.find(t => t.value === formData.tipo)?.label || ''}`, 14, 25);

    const ahora = new Date();
    const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const fechaFormateada = ahora.toLocaleDateString('es-DO', opciones);
    doc.text(`Fecha: ${fechaFormateada}`, 14, 30);

    // Encabezado derecho
    const pageWidth = doc.internal.pageSize.width;
    doc.text(`Capital: ${formatMoney(calculado.capital)}`, pageWidth - 14, 15, { align: 'right' });
    doc.text(`Interés: ${formatMoney(calculado.interes)}`, pageWidth - 14, 20, { align: 'right' });
    doc.text(`Tasa: ${formData.tasa}%`, pageWidth - 14, 25, { align: 'right' });
    doc.text(`Total: ${formatMoney(calculado.total)}`, pageWidth - 14, 30, { align: 'right' });
    doc.text(`Cantidad de pagos: ${formData.cuotas}`, pageWidth - 14, 35, { align: 'right' });
    doc.text(`Monto de la cuota: ${formatMoney(calculado.cuota)}`, pageWidth - 14, 40, { align: 'right' });

    // Título
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Tabla Amortización', pageWidth / 2, 50, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Desglose de las cuotas a pagar del préstamo.', pageWidth / 2, 56, { align: 'center' });

    // Preparar datos para la tabla
    const tableData = tablaAmortizacion.map(row => [
      row.pago,
      row.cuota,
      row.capital,
      row.interes,
      row.saldo,
      `${row.fecha}\n${row.diaSemana}`
    ]);

    // Generar tabla con autoTable
    autoTable(doc, {
      startY: 62,
      head: [['Pago', 'Cuota', 'Capital', 'Interés', 'Saldo', 'Fecha']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [52, 152, 219],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center'
      },
      columnStyles: {
        5: { halign: 'left', cellPadding: { left: 3 } }
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      }
    });

    // Generar nombre de archivo
    const fechaHora = new Date().toISOString().replace(/[:.]/g, '-').split('T');
    const nombreArchivo = `INVERSIONES RAMIREZ - Tabla Amortización_${fechaHora[0]}_${fechaHora[1].substring(0, 6).replace(/-/g, '')}.pdf`;

    // Guardar PDF
    doc.save(nombreArchivo);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Limpiar errores previos
    setError('');

    // Validaciones más específicas
    if (!formData.cliente_id) {
      setError('❌ Debe seleccionar un cliente primero');
      return;
    }

    if (!formData.monto_prestado || parseFloat(formData.monto_prestado) <= 0) {
      setError('❌ El capital debe ser mayor a 0');
      return;
    }

    // Para tipos que NO son Capital Final, validar cuotas
    if (formData.tipo !== 'capital_final' && formData.tipo !== 'capital_final_fijo') {
      if (!formData.cuotas || parseInt(formData.cuotas) <= 0) {
        setError('❌ Debe especificar la cantidad de pagos (cuotas) para este tipo de préstamo');
        return;
      }
    }

    // Validar tasa solo si el tipo de préstamo lo requiere
    if (shouldShowField('tasa') && (!formData.tasa || formData.tasa === '')) {
      setError('❌ La tasa es requerida para este tipo de préstamo');
      return;
    }

    // CONFIRMACIÓN ANTES DE GUARDAR
    const tipoLabel = tiposPrestamo.find(t => t.value === formData.tipo)?.label || formData.tipo;
    const nombreCliente = clienteSeleccionado ? 
      `${clienteSeleccionado.nombres} ${clienteSeleccionado.apellidos}` : 
      'Cliente seleccionado';
    
    const confirmacion = window.confirm(
      `¿CREAR NUEVO PRÉSTAMO?\n\n` +
      `Cliente: ${nombreCliente.toUpperCase()}\n` +
      `Modalidad: ${formData.modalidad}\n` +
      `Tipo: ${tipoLabel}\n` +
      `Capital: ${formatMoney(parseFloat(formData.monto_prestado))}\n` +
      `Tasa: ${formData.tasa || 0}%\n` +
      `Interés: ${formatMoney(calculado.interes)}\n` +
      `Total: ${formatMoney(calculado.total)}\n` +
      `Cuota: ${formatMoney(calculado.cuota)}\n` +
      `Gastos: ${formatMoney(parseFloat(formData.gastos_cierre))}\n` +
      `Capital a entregar: ${formatMoney(calculado.capital_entregar)}\n\n` +
      `¿Desea continuar?`
    );

    if (!confirmacion) {
      setError('❌ Creación de préstamo cancelada por el usuario');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const prestamoData = {
        cliente_id: parseInt(formData.cliente_id),
        ruta_id: formData.ruta_id ? parseInt(formData.ruta_id) : null,
        monto_prestado: parseFloat(formData.monto_prestado),
        tasa: parseFloat(formData.tasa) || 0,
        cuotas: (formData.tipo === 'capital_final' || formData.tipo === 'capital_final_fijo') ? 1 : parseInt(formData.cuotas),
        modalidad: formData.modalidad,
        tipo: formData.tipo,
        fecha_inicio: formData.fecha_inicio,
        fecha_efectividad: formData.fecha_efectividad,
        gastos_cierre: parseFloat(formData.gastos_cierre) || 0
      };

      await prestamoService.createPrestamo(prestamoData);

      setSuccess('✅ PRÉSTAMO CREADO EXITOSAMENTE');

      // Mostrar alerta de éxito
      setTimeout(() => {
        window.alert(
          `✅ Préstamo creado correctamente\n\n` +
          `Cliente: ${nombreCliente}\n` +
          `Monto: ${formatMoney(parseFloat(formData.monto_prestado))}\n` +
          `Total a pagar: ${formatMoney(calculado.total)}`
        );
        
        navigate(getRoute('/prestamos/lista'));
      }, 300);

    } catch (err) {
      console.error('Error al crear préstamo:', err);
      setError(err.response?.data?.error || 'Error al crear el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Nuevo Préstamo</h1>
        <button
          className="btn"
          onClick={() => navigate(getRoute('/prestamos'))}
          style={{background: '#666'}}
        >
          ← Volver
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      <form onSubmit={handleSubmit}>
        {/* Información General */}
        <div className="card" style={{marginBottom: '2rem'}}>
          <div className="card-title" style={{background: '#27ae60'}}>
            Información General
          </div>
          <div className="form-grid">
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Cliente *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Buscar por nombre, cédula o apodo..."
                  value={busquedaCliente}
                  onChange={(e) => {
                    setBusquedaCliente(e.target.value);
                    setMostrarDropdown(true);
                    if (!e.target.value) {
                      limpiarCliente();
                    }
                  }}
                  onFocus={() => setMostrarDropdown(true)}
                  className="form-control"
                  style={{
                    paddingRight: clienteSeleccionado ? '40px' : '10px'
                  }}
                />
                {clienteSeleccionado && (
                  <button
                    type="button"
                    onClick={limpiarCliente}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Dropdown de resultados */}
              {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  border: '2px solid #2196F3',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  marginTop: '4px'
                }}>
                  {clientesFiltrados.map(cliente => (
                    <div
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {/* Foto del cliente */}
                      {cliente.foto_url ? (
                        <img
                          src={cliente.foto_url}
                          alt={cliente.nombres}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                        </div>
                      )}

                      {/* Información del cliente */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                          {cliente.nombres} {cliente.apellidos}
                        </div>
                        {cliente.apodo && (
                          <div style={{ fontSize: '0.85rem', color: '#2196F3', fontStyle: 'italic' }}>
                            "{cliente.apodo}"
                          </div>
                        )}
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          Cédula: {cliente.cedula || 'N/A'} | ID: {cliente.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Mensaje cuando no hay resultados */}
              {mostrarDropdown && busquedaCliente && clientesFiltrados.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '4px',
                  textAlign: 'center',
                  color: '#666',
                  zIndex: 1000
                }}>
                  No se encontraron clientes
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Ruta</label>
              <select
                name="ruta_id"
                value={formData.ruta_id}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">--- Seleccione ---</option>
                {rutas.map(ruta => (
                  <option key={ruta.id} value={ruta.id}>
                    {ruta.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Plazo */}
        <div className="card" style={{marginBottom: '2rem'}}>
          <div className="card-title" style={{background: '#27ae60'}}>
            Plazo
          </div>
          <div className="form-subtitle">
            Indique la modalidad y tipo del préstamo.
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Modalidad *</label>
              <select
                name="modalidad"
                value={formData.modalidad}
                onChange={handleChange}
                required
                className="form-control"
              >
                {modalidades.map(mod => (
                  <option key={mod.value} value={mod.value}>
                    {mod.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tipo Préstamo *</label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                required
                className="form-control"
              >
                {getTiposPrestamoPorModalidad().map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Días Libres - Solo mostrar para préstamos Diarios */}
        {formData.modalidad === 'Diario' && (
          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#27ae60'}}>
              Días Libres
            </div>
            <div className="form-subtitle">
              Seleccione máximo dos (2) días libres de cobro para el préstamo.
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', padding: '15px 0'}}>
              {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(dia => {
                const isSelected = (formData.dias_libres || []).includes(dia);
                return (
                  <div key={dia} style={{textAlign: 'center'}}>
                    <div style={{marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem'}}>{dia}</div>
                    <button
                      type="button"
                      onClick={() => handleDiaLibreToggle(dia)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: isSelected ? '#27ae60' : '#e74c3c',
                        color: 'white',
                        opacity: isSelected ? 1 : 0.8
                      }}
                    >
                      {isSelected ? 'Sí' : 'No'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Desembolso */}
        <div className="card" style={{marginBottom: '2rem'}}>
          <div className="card-title" style={{background: '#27ae60'}}>
            Desembolso
          </div>
          <div className="form-subtitle">
            Indique el capital y la tasa, si aplica, del préstamo.
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Capital *</label>
              <input
                type="text"
                name="monto_prestado"
                value={formatNumberWithCommas(formData.monto_prestado)}
                onChange={(e) => {
                  const value = removeCommas(e.target.value);
                  if (value === '' || /^\d+\.?\d*$/.test(value)) {
                    handleChange({ target: { name: 'monto_prestado', value } });
                  }
                }}
                placeholder="Ingrese monto"
                required
                className="form-control"
              />
            </div>

            {shouldShowField('tasa') && (
              <div className="form-group">
                <label>Tasa {shouldShowField('tasa') ? '*' : ''}</label>
                <input
                  type="number"
                  name="tasa"
                  value={formData.tasa}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  required={shouldShowField('tasa')}
                  className="form-control"
                />
              </div>
            )}

            {shouldShowField('interes') && (
              <div className="form-group">
                <label>Interés</label>
                <input
                  type="text"
                  value={formatMoney(calculado.interes)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>
            )}

            {shouldShowField('total') && (
              <div className="form-group">
                <label>Total</label>
                <input
                  type="text"
                  value={formatMoney(calculado.total)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>
            )}
          </div>
        </div>

        {/* Cálculos */}
        <div className="card" style={{marginBottom: '2rem'}}>
          <div className="card-title" style={{background: '#3498db'}}>
            Cálculos
          </div>

          {shouldShowField('cuotas') && (
            <div className="form-group" style={{padding: '1.5rem'}}>
              <label>Cuota</label>
              <div className="form-subtitle">
                Indique la cantidad de pagos y el monto de la cuota a cobrar.
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem'}}>
                <div>
                  <label style={{fontSize: '0.85rem', color: '#666'}}>Cantidad de pagos *</label>
                  <input
                    type="number"
                    name="cuotas"
                    value={formData.cuotas}
                    onChange={handleChange}
                    placeholder="Ingrese valor"
                    min="1"
                    required
                    className="form-control"
                  />
                </div>
                <div>
                  <label style={{fontSize: '0.85rem', color: '#666'}}>Monto de la cuota</label>
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <input
                      type="text"
                      name="cuota_manual"
                      value={formData.tipo === 'tipo_san' ? formData.cuota_manual || '' : formatMoney(calculado.cuota)}
                      onChange={formData.tipo === 'tipo_san' ? handleCuotaManualChange : undefined}
                      disabled={true}
                      className="form-control"
                      style={{background: '#f5f5f5', flex: 1, fontWeight: 'bold'}}
                      placeholder=""
                      readOnly
                    />
                    {(formData.tipo === 'tipo_san' || formData.tipo === 'cuota_fija') && (
                      <button
                        type="button"
                        onClick={calcularPrestamo}
                        style={{
                          background: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Calcular
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-group" style={{marginTop: '1.5rem'}}>
            <label>Gastos de Cierre</label>
            <div className="form-subtitle">
              Si tiene gastos de cierre especifique el monto.
            </div>
            <input
              type="number"
              name="gastos_cierre"
              value={formData.gastos_cierre}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              className="form-control"
              style={{maxWidth: '300px', marginTop: '0.5rem'}}
            />
          </div>

          <div className="form-group" style={{marginTop: '1.5rem'}}>
            <label>Capital a Entregar</label>
            <input
              type="text"
              value={formatMoney(calculado.capital_entregar)}
              disabled
              className="form-control"
              style={{maxWidth: '300px', background: '#e8f5e9', fontWeight: 'bold', fontSize: '1.1rem'}}
            />
          </div>

          {/* Botón Mostrar Tabla Amortización */}
          <div style={{marginTop: '2rem', textAlign: 'center'}}>
            <button
              type="button"
              onClick={mostrarTabla ? ocultarTabla : generarTablaAmortizacion}
              style={{
                background: '#16a085',
                color: 'white',
                border: 'none',
                padding: '12px 40px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '0 auto'
              }}
            >
              <span style={{fontSize: '20px'}}>📊</span>
              {mostrarTabla ? 'Ocultar Tabla Amortización' : 'Mostrar Tabla Amortización'}
            </button>
          </div>

          {/* Tabla de Amortización */}
          {mostrarTabla && tablaAmortizacion.length > 0 && (
            <div style={{marginTop: '2rem'}}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{margin: 0, color: '#2c3e50'}}>Tabla Amortización</h3>
                <button
                  type="button"
                  onClick={descargarPDF}
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>⬇️</span>
                  Descargar
                </button>
              </div>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '1rem'}}>
                Desglose de las cuotas a pagar del préstamo.
              </p>
              <div style={{overflowX: 'auto'}}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <thead>
                    <tr style={{background: '#3498db', color: 'white'}}>
                      <th style={{padding: '12px', textAlign: 'center', borderRight: '1px solid #2980b9'}}>Pago</th>
                      <th style={{padding: '12px', textAlign: 'center', borderRight: '1px solid #2980b9'}}>Cuota</th>
                      <th style={{padding: '12px', textAlign: 'center', borderRight: '1px solid #2980b9'}}>Capital</th>
                      <th style={{padding: '12px', textAlign: 'center', borderRight: '1px solid #2980b9'}}>Interés</th>
                      <th style={{padding: '12px', textAlign: 'center', borderRight: '1px solid #2980b9'}}>Saldo</th>
                      <th style={{padding: '12px', textAlign: 'left', paddingLeft: '20px'}}>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tablaAmortizacion.map((row, index) => (
                      <tr key={index} style={{
                        background: index % 2 === 0 ? '#f8f9fa' : 'white',
                        borderBottom: '1px solid #ddd'
                      }}>
                        <td style={{padding: '10px', textAlign: 'center'}}>{row.pago}</td>
                        <td style={{padding: '10px', textAlign: 'center'}}>{row.cuota}</td>
                        <td style={{padding: '10px', textAlign: 'center'}}>{row.capital}</td>
                        <td style={{padding: '10px', textAlign: 'center'}}>{row.interes}</td>
                        <td style={{padding: '10px', textAlign: 'center'}}>{row.saldo}</td>
                        <td style={{padding: '10px', textAlign: 'left', paddingLeft: '20px'}}>
                          {row.fecha}<br/>
                          <strong>{row.diaSemana}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Fecha */}
        <div className="card" style={{marginBottom: '2rem'}}>
          <div className="card-title" style={{background: '#3498db'}}>
            Fecha
          </div>
          <div className="form-subtitle">
            Seleccione la(s) fecha(s) de pago para el préstamo.
          </div>

          {/* Mora */}
          <div className="form-group" style={{marginBottom: '1.5rem'}}>
            <label>Mora</label>
            <div className="form-subtitle">
              Indique si tiene mora.
            </div>
            <div style={{display: 'flex', gap: '1rem', marginTop: '0.5rem'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                <input
                  type="radio"
                  name="tiene_mora"
                  checked={formData.tiene_mora === true}
                  onChange={() => setFormData(prev => ({...prev, tiene_mora: true}))}
                  style={{cursor: 'pointer'}}
                />
                <span>Sí</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                background: formData.tiene_mora === false ? '#e74c3c' : 'transparent',
                color: formData.tiene_mora === false ? 'white' : 'inherit',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                transition: 'all 0.3s'
              }}>
                <input
                  type="radio"
                  name="tiene_mora"
                  checked={formData.tiene_mora === false}
                  onChange={() => setFormData(prev => ({...prev, tiene_mora: false}))}
                  style={{cursor: 'pointer'}}
                />
                <span>No</span>
              </label>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>
                {formData.tipo === 'capital_final_fijo' ? 'Primera Quincena *' : 'FECHA DE DESEMBOLSO *'}
              </label>
              <input
                type="date"
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
                required
                className="form-control"
              />
              <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                📤 Fecha en que se entrega el dinero al cliente
              </small>
            </div>

            <div className="form-group">
              <label>
                {formData.tipo === 'capital_final_fijo' ? 'Segunda Quincena *' : 'FECHA DEL PRIMER PAGO *'}
              </label>
              <input
                type="date"
                name="fecha_efectividad"
                value={formData.fecha_efectividad}
                onChange={handleChange}
                required
                className="form-control"
              />
              <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                📥 Fecha en que el cliente debe pagar su primera cuota
              </small>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
          <button
            type="button"
            onClick={() => navigate(getRoute('/prestamos'))}
            className="btn"
            style={{background: '#95a5a6'}}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn"
            style={{background: '#27ae60'}}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NuevoPrestamo;
